'use client'
import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react'
import { useMediaQuery } from 'react-responsive'
import { cn } from '../../helpers/utils.js'
import { withLayout } from '../../shared/Styles.js'
import { Heading, Text, Picture } from '../../components/index.js'
import { useExposure } from '../../hooks/useExposure.js'
import type { MediaSceneSwitcherProps, MediaSceneSwitcherItem } from './types.js'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay } from 'swiper/modules'
import 'swiper/css'
import type { Theme } from '../../types/props.js'

const componentType = 'media'
const componentName = 'media_scene_switcher'
const INTERVAL_TIME = 3000

interface ItemProps {
  data: MediaSceneSwitcherItem
  configuration?: {
    index: number
    isActive?: boolean
    onItemClick?: (index: number) => void
  }
  theme?: Theme
}

const DesktopItem = ({ data, configuration, theme }: ItemProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const isActive = configuration?.isActive || false

  useExposure(ref, {
    componentType,
    componentName,
    componentTitle: data?.title,
    position: (configuration?.index ?? 0) + 1,
  })

  return (
    <div
      ref={ref}
      className={cn(
        'media-scene-switcher-item rounded-box cursor-pointer overflow-hidden transition-colors',
        'relative text-[#6D6D6F]',
        isActive ? 'opacity-100' : 'opacity-60',
        {
          'bg-[#1D1D1F] text-white': isActive,
          'bg-[#EAEAEC] text-[1D1D1F]': isActive && theme === 'light',
        }
      )}
      onClick={() => configuration?.onItemClick?.(configuration?.index ?? 0)}
    >
      <div className="media-scene-switcher-item-content laptop:gap-[32px] laptop:p-[12px] desktop:gap-[48px] desktop:p-[24px] flex items-center justify-between gap-[24px]">
        <div>
          <div className="media-scene-switcher-item-title flex-1">
            <Heading as="h6" size={2} html={data?.title} />
          </div>
          {data?.description && isActive && (
            <Text
              size={2}
              className="media-scene-switcher-item-description tablet:text-base desktop:text-lg mt-1 text-sm font-bold"
              html={data.description}
            />
          )}
        </div>

        <div
          className={cn(
            'media-scene-switcher-item-badge rounded-btn border-[1.6px] px-[18px] py-[7px]',
            theme === 'dark' ? 'border-[#B5B7BB]' : 'border-[#4A4C56]',
            {
              '!border-[#F5F6F7]': theme === 'dark' && isActive,
            }
          )}
        >
          <Heading as="h6" size={2} html={data?.tag} />
        </div>
      </div>
      <div
        className={cn('media-scene-switcher-progress absolute bottom-0 left-0 h-[2px] w-full', {
          'media-scene-switcher-progress-active': isActive,
        })}
        style={{
          transform: 'translate3d(-100%, 0, 0)',
          background: 'linear-gradient(90deg, #3ad1ff 0%, #008cd6 100%)',
          animation: isActive ? `progress-bar ${INTERVAL_TIME}ms ease-out` : 'none',
        }}
      />
    </div>
  )
}

const MobileItem = ({ data, configuration, theme }: ItemProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const isActive = configuration?.isActive || false
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' })

  useExposure(ref, {
    componentType,
    componentName,
    componentTitle: data?.title,
    position: (configuration?.index ?? 0) + 1,
  })

  const videoUrl = isMobile && data?.mobVideoUrl?.url ? data.mobVideoUrl.url : data?.videoUrl?.url
  const imageUrl = isMobile && data?.mobImageUrl?.url ? data.mobImageUrl.url : data?.imageUrl?.url
  const posterUrl = imageUrl || data?.videoUrl?.thumbnailURL || ''

  return (
    <div
      ref={ref}
      className={cn(
        'media-scene-switcher-mobile-item rounded-box flex h-[360px] w-[296px] max-w-full flex-col overflow-hidden',
        {
          'aiui-dark': theme === 'dark',
        }
      )}
    >
      <div className="media-scene-switcher-mobile-media relative aspect-[554/480] w-full overflow-hidden">
        {videoUrl ? (
          <video src={videoUrl} playsInline autoPlay loop muted poster={posterUrl} className="size-full object-cover" />
        ) : imageUrl ? (
          <Picture
            className="size-full"
            imgClassName="size-full object-cover"
            source={imageUrl}
            alt={data?.title || ''}
          />
        ) : null}
      </div>
      <div
        className={cn(
          'media-scene-switcher-mobile-bottom flex items-start justify-between gap-[8px] p-[16px]',
          'text-[#6D6D6F]',
          {
            'bg-[#1D1D1F] text-white': isActive,
            'bg-[#EAEAEC] text-[1D1D1F]': isActive && theme === 'light',
          }
        )}
      >
        <div>
          <div className="media-scene-switcher-mobile-title line-clamp-3 h-[72px] flex-1">
            <Heading as="h6" size={2} html={data?.title} />
          </div>
          {data?.description && isActive && (
            <Text
              size={2}
              className="media-scene-switcher-item-description tablet:text-base desktop:text-lg mt-1 line-clamp-3 text-sm font-bold"
              html={data.description}
            />
          )}
        </div>
        <div
          className={cn(
            'media-scene-switcher-mobile-badge rounded-btn shrink-0 border-[1.6px] px-[12px] py-[5px]',
            theme === 'dark' ? 'border-[#B5B7BB]' : 'border-[#4A4C56]',
            {
              '!border-[#F5F6F7]': theme === 'dark' && isActive,
            }
          )}
        >
          <Heading as="h6" size={1} html={data?.tag} />
        </div>
      </div>
      <div
        className={cn('media-scene-switcher-progress h-[2px] w-full', {
          'media-scene-switcher-progress-active': isActive,
        })}
        style={{
          transform: 'translate3d(-100%, 0, 0)',
          background: 'linear-gradient(90deg, #3ad1ff 0%, #008cd6 100%)',
          animation: isActive ? `progress-bar ${INTERVAL_TIME}ms ease-out` : 'none',
        }}
      />
    </div>
  )
}

const MediaSceneSwitcher = forwardRef<HTMLDivElement, MediaSceneSwitcherProps>(({ className = '', data, id }, ref) => {
  const { title, subtitle, items = [], theme = 'light', layout, titleIcon } = data || {}
  const [currentIndex, setCurrentIndex] = useState(0)
  const [activeIndex, setActiveIndex] = useState(0)
  const innerRef = useRef<HTMLDivElement>(null)
  const intervalRef = useRef<number>(0)
  const isMobile = useMediaQuery({ query: '(max-width: 1023px)' })

  useImperativeHandle(ref, () => innerRef.current as HTMLDivElement)

  useExposure(innerRef, {
    componentType,
    componentName,
    componentTitle: title,
  })

  // Auto-play functionality for desktop
  useEffect(() => {
    if (isMobile || items.length === 0) return

    intervalRef.current = window.setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % items.length)
    }, INTERVAL_TIME)

    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current)
      }
    }
  }, [isMobile, items.length])

  const handleItemClick = (index: number) => {
    setCurrentIndex(index)
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current)
    }
    intervalRef.current = window.setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % items.length)
    }, INTERVAL_TIME)
  }

  const currentItem = items[currentIndex]
  const videoUrl = currentItem?.videoUrl?.url
  const imageUrl = currentItem?.imageUrl?.url
  const posterUrl = imageUrl || currentItem?.videoUrl?.thumbnailURL || ''

  return (
    <>
      <style>{`
          @keyframes progress-bar {
            from {
              transform: translate3d(-100%, 0, 0);
            }
            to {
              transform: translate3d(0, 0, 0);
            }
          }
        `}</style>
      <section
        id={id}
        ref={innerRef}
        className={cn(
          'media-scene-switcher text-info-primary w-full',
          {
            'aiui-dark': theme === 'dark',
          },
          className
        )}
      >
        {/* Desktop Layout */}
        <div className="media-scene-switcher-desktop laptop:gap-[24px] lg-desktop:gap-[40px] laptop:flex hidden w-full items-stretch gap-[20px] overflow-hidden">
          <div
            className={cn(
              'media-scene-switcher-preview rounded-box laptop:flex-1 relative aspect-[824/640] max-w-[824px] shrink-0 overflow-hidden',
              {
                'order-1': layout === 'right',
              }
            )}
          >
            {items.map((item, index) => {
              const itemVideoUrl = item?.videoUrl?.url
              const itemImageUrl = item?.imageUrl?.url
              const itemPosterUrl = itemImageUrl || item?.videoUrl?.thumbnailURL || ''

              return (
                <div
                  key={item.id || index}
                  className={cn(
                    'media-scene-switcher-media rounded-box absolute inset-0 hidden size-full overflow-hidden',
                    {
                      'inline-block': index === currentIndex,
                    }
                  )}
                >
                  {itemVideoUrl ? (
                    <video
                      src={itemVideoUrl}
                      playsInline
                      autoPlay
                      loop
                      muted
                      poster={itemPosterUrl}
                      className="size-full object-cover"
                    />
                  ) : itemImageUrl ? (
                    <Picture
                      className="size-full"
                      imgClassName="size-full object-cover"
                      source={itemImageUrl}
                      alt={item?.title || ''}
                    />
                  ) : null}
                </div>
              )
            })}
          </div>

          <div className="media-scene-switcher-sidebar laptop:flex-1 flex shrink-0 flex-col justify-between">
            <div className="media-scene-switcher-header flex flex-col">
              <div className="desktop:gap-2 flex items-center gap-1">
                {title && (
                  <Heading
                    as="h3"
                    html={title}
                    size={4}
                    className="media-scene-switcher-title text-info-primary tablet:!text-[40px] desktop:!text-[56px] lg-desktop:!text-[64px] text-[40px] leading-none text-[#00BEFA]"
                  />
                )}
                {titleIcon && (
                  <Picture
                    source={titleIcon.url}
                    alt={titleIcon.alt || 'title icon'}
                    className="desktop:h-8 lg-desktop:h-10 h-6"
                    imgClassName="!w-auto h-full"
                  />
                )}
              </div>

              {subtitle && (
                <Text
                  as="span"
                  size={4}
                  html={subtitle}
                  className="media-scene-switcher-subtitle tablet:text-[14px] laptop:text-[14px] desktop:text-[16px] lg-desktop:text-[18px] relative -top-2 mt-3 text-[14px]"
                />
              )}
            </div>

            <div className="media-scene-switcher-list flex flex-col gap-[16px]">
              {items.map((item, index) => (
                <DesktopItem
                  key={item.id || index}
                  data={item}
                  configuration={{
                    index,
                    isActive: index === currentIndex,
                    onItemClick: handleItemClick,
                  }}
                  theme={theme}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="media-scene-switcher-mobile laptop:hidden flex flex-col overflow-visible">
          <div className="media-scene-switcher-mobile-header">
            <div className="desktop:gap-2 flex items-center gap-1">
              {title && (
                <Heading
                  as="h2"
                  html={title}
                  size={2}
                  className="media-scene-switcher-title tablet:!text-[40px] desktop:!text-[56px] lg-desktop:!text-[64px] text-[40px] leading-tight text-[#00BEFA]"
                />
              )}
              {titleIcon && (
                <Picture
                  source={titleIcon.url}
                  alt={titleIcon.alt || 'title icon'}
                  className="desktop:h-8 lg-desktop:h-10 h-6"
                  imgClassName="!w-auto h-full"
                />
              )}
            </div>
            {subtitle && (
              <Text as="span" size={4} html={subtitle} className="media-scene-switcher-subtitle text-[14px]" />
            )}
          </div>

          <div className="media-scene-switcher-mobile-swiper mt-[24px] overflow-visible">
            <Swiper
              onSlideChange={swiper => setActiveIndex(swiper.realIndex)}
              initialSlide={0}
              modules={[Autoplay]}
              loop={items.length > 1}
              autoplay={items.length > 1 ? { delay: INTERVAL_TIME, disableOnInteraction: false } : false}
              spaceBetween={12}
              slidesPerView="auto"
              watchSlidesProgress={true}
              className="w-full !overflow-visible"
            >
              {items.map((item, index) => (
                <SwiperSlide key={item.id || index} className="!h-auto !w-[296px] max-w-full">
                  <MobileItem
                    data={item}
                    configuration={{
                      index,
                      isActive: index === activeIndex,
                    }}
                    theme={theme}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </section>
    </>
  )
})

MediaSceneSwitcher.displayName = 'MediaSceneSwitcher'

export default withLayout(MediaSceneSwitcher, { style: 'overflow: hidden;' })
